import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, Sparkles, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "../lib/api";
import { NeonLogo } from "../components/common";
import { Button } from "../components/ui/button";

const C = { cyan: "#00FFD1", magenta: "#FF2EA6" };

const PLAN_META = {
  maand: { tag: "Flexibel", popular: false, glow: C.cyan, features: ["Onbeperkt oefeningen", "Lezen & rekenen", "1 kind", "Maandelijks opzegbaar"] },
  jaar: { tag: "Beste deal", popular: true, glow: C.magenta, features: ["Onbeperkt oefeningen", "Lezen & rekenen", "Ouderdashboard & grafieken", "2 maanden gratis"] },
  gezin: { tag: "Gezin", popular: false, glow: C.cyan, features: ["Onbeperkt oefeningen", "Tot 4 kinderen", "Volledig ouderdashboard", "Prioriteit support"] },
};

export default function Pricing() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    api.get("/packages").then(({ data }) => setPackages(data.packages)).catch(() => {});
  }, []);

  const checkout = async (id) => {
    setLoading(id);
    try {
      const { data } = await api.post("/payments/checkout", { package_id: id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen font-outfit text-white relative overflow-x-hidden" style={{ background: "#050507" }}>
      <div className="glow-orb" style={{ width: 480, height: 480, background: C.cyan, top: -140, left: -100 }} />
      <div className="glow-orb" style={{ width: 420, height: 420, background: C.magenta, top: 100, right: -120 }} />
      <div className="absolute inset-0 neon-grid opacity-50 pointer-events-none" />

      <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-white/10" style={{ background: "rgba(10,10,15,0.8)" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <NeonLogo />
          <Button variant="ghost" className="rounded-full font-bold text-white hover:bg-white/10 hover:text-white" onClick={() => navigate("/")} data-testid="pricing-home-btn"><ArrowLeft className="w-4 h-4 mr-1" /> Home</Button>
        </div>
      </header>

      <section className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-16 text-center">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#00FFD1]/40 text-[#00FFD1] font-bold text-sm mb-5 box-glow-cyan" style={{ background: "rgba(0,255,209,0.06)" }}><Sparkles className="w-4 h-4" /> Prijzen</span>
        <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tight">Kies je <span className="neon-gradient-text">avontuur</span></h1>
        <p className="mt-4 text-lg text-slate-400 max-w-xl mx-auto">Start gratis en upgrade wanneer je wilt. Stop op elk moment.</p>

        <div className="mt-14 grid md:grid-cols-3 gap-6 text-left">
          {packages.map((p) => {
            const meta = PLAN_META[p.id] || { features: [], glow: C.cyan };
            return (
              <div key={p.id} className="relative rounded-3xl p-8 border-2 transition-all hover:-translate-y-1"
                style={{ background: "#0A0A0F", borderColor: meta.popular ? meta.glow : "rgba(255,255,255,0.1)", boxShadow: meta.popular ? `0 0 34px ${meta.glow}45` : "none" }}
                data-testid={`plan-${p.id}`}>
                {meta.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-black font-bold text-sm flex items-center gap-1 box-glow-magenta" style={{ background: meta.glow }}><Crown className="w-4 h-4" /> Populair</span>
                )}
                <p className="font-bold text-sm uppercase tracking-wider text-slate-500">{meta.tag}</p>
                <h3 className="font-display text-2xl font-extrabold mt-1">{p.name}</h3>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-display text-4xl font-black" style={{ color: meta.glow }}>€{p.amount.toFixed(2)}</span>
                  <span className="text-slate-500 font-semibold mb-1">/{p.id === "jaar" ? "jaar" : "mnd"}</span>
                </div>
                <ul className="mt-6 space-y-3">
                  {meta.features.map((f) => (
                    <li key={f} className="flex items-center gap-3 font-semibold text-slate-300"><div className="w-6 h-6 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(57,255,20,0.15)" }}><Check className="w-4 h-4 text-[#39FF14]" /></div>{f}</li>
                  ))}
                </ul>
                <Button onClick={() => checkout(p.id)} disabled={loading === p.id} data-testid={`subscribe-${p.id}`}
                  className="mt-8 w-full h-12 rounded-full font-bold text-black border-0 active:scale-95 hover:opacity-90" style={{ background: meta.glow, boxShadow: `0 0 20px ${meta.glow}55` }}>
                  {loading === p.id ? "Bezig..." : "Abonneer nu"}
                </Button>
              </div>
            );
          })}
        </div>
        <p className="mt-10 text-sm text-slate-600 font-medium">Betalingen worden veilig verwerkt via Stripe.</p>
      </section>
    </div>
  );
}
